import React, { useEffect, useRef } from 'react';
import "./DilemaBarGraph.css";

// ─────────────────────────────────────────────────────────────────────────────
// STYLE INSPECTOR
// ─────────────────────────────────────────────────────────────────────────────
const INSPECT_PROPS = [
  'width','height','maxWidth','minWidth','minHeight',
  'display','flexDirection','flexWrap','flex','alignItems','justifyContent',
  'float','position','boxSizing',
  'paddingTop','paddingRight','paddingBottom','paddingLeft',
  'marginTop','marginRight','marginBottom','marginLeft',
  'overflow','overflowX','overflowY',
  'transform','transformOrigin','gridTemplateColumns',
];

export function logComputedStyles(rootEl, label = 'section') {
  if (!rootEl) { console.warn('[StyleInspector] rootEl is null'); return; }

  console.groupCollapsed(`%c[StyleInspector] ${label}`, 'color:#9c27b0;font-weight:bold');

  const walk = (el, depth) => {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return;

    const cs  = window.getComputedStyle(el);
    const w   = parseFloat(cs.width);
    if (!w || w < 2) return;

    const styles = {};
    INSPECT_PROPS.forEach(p => {
      const v = cs[p];
      if (v && v !== 'none' && v !== 'normal' && v !== 'auto' && v !== '0px') {
        styles[p] = v;
      }
    });

    Array.from(el.children).forEach(child => walk(child, depth + 1));
  };

  walk(rootEl, 0);
  console.groupEnd();
}


// ─────────────────────────────────────────────────────────────────────────────
// DILEMA BAR GRAPH
// ─────────────────────────────────────────────────────────────────────────────
function DilemaBarGraph({ graph_data, blur, axisColor = '#c98fe3' }) {

  const canvasRef  = useRef(null);
  const wrapperRef = useRef(null);

  const draw = (forceWidth = null) => {
    if (!graph_data || graph_data.length === 0) return;

    const canvas  = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const width  = forceWidth ?? (wrapper.offsetWidth || 320);
    const height = 180;

    canvas.width  = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    // ── Responsive font sizes based on width breakpoints ──
    let labelFontSize;
    let percentFontSize;

    if (width < 480) {
      labelFontSize   = 12;
      percentFontSize = 12;
    } else if (width < 767) {
      labelFontSize   = 13;
      percentFontSize = 13;
    } else {
      labelFontSize   = 14;
      percentFontSize = 13;
    }

    // ── Responsive padding ────────────────────────────────
    const paddingBottom = Math.max(42, Math.floor(width * 0.13));
    const paddingTop    = 10;
    const chartHeight   = height - paddingBottom - paddingTop;

    const maxValue  = Math.max(...graph_data.map(i => i.value));
    const barCount  = graph_data.length;
    const barWidth  = Math.floor((width / barCount) * 0.5);
    const barGap    = Math.floor((width / barCount) * 0.5);

    ctx.clearRect(0, 0, width, height);

    // ── Grid lines (10% to 100%) ──────────────────────────
    ctx.strokeStyle = '#d8c9f1';
    ctx.lineWidth   = 1;
    for (let pct = 10; pct <= 100; pct += 10) {
      const y = paddingTop + chartHeight - (pct / 100) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // ── Y Axis ────────────────────────────────────────────
    const yAxisX = 0;
    ctx.strokeStyle = axisColor;
    ctx.lineWidth   = 2;
    ctx.beginPath();
    ctx.moveTo(yAxisX, paddingTop);
    ctx.lineTo(yAxisX, paddingTop + chartHeight);
    ctx.stroke();

    // ── X Axis ────────────────────────────────────────────
    ctx.beginPath();
    ctx.moveTo(yAxisX, paddingTop + chartHeight);
    ctx.lineTo(width, paddingTop + chartHeight);
    ctx.stroke();

    // ── Bars + Labels ─────────────────────────────────────
    graph_data.forEach((bar, index) => {
      const barHeight = Math.round((bar.value / maxValue) * chartHeight);
      const x         = index * (barWidth + barGap) + Math.floor(barGap / 2);
      const y         = paddingTop + (chartHeight - barHeight);

      // Bar
      ctx.fillStyle = bar.color || 'rgb(163,128,193)';
      ctx.beginPath();
      const r = 4;
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + barWidth - r, y);
      ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + r);
      ctx.lineTo(x + barWidth, y + barHeight);
      ctx.lineTo(x, y + barHeight);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.fill();

      const centerX = x + barWidth / 2;

      // Letter label — sits in upper portion of paddingBottom
      const letterY = paddingTop + chartHeight + paddingBottom * 0.38;
      ctx.fillStyle = bar.color || '#8a409c';
      ctx.font      = `bold ${labelFontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(bar.letter, centerX, letterY);

      // Percentage label — sits below the letter with clear gap
      const percentY = paddingTop + chartHeight + paddingBottom * 0.75;
      const percentage = Math.round((bar.value / maxValue) * 100);
      ctx.fillStyle = '#8a409c';
      ctx.font      = `${percentFontSize}px Arial, sans-serif`;
      ctx.fillText(`${percentage}%`, centerX, percentY);
    });
  };

  // Normal responsive draw
  useEffect(() => { draw(); }, [graph_data]);

  // Attach redrawAt() for capture usage
  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.__redrawAt = (w) => draw(w);
    }
  }, [graph_data]);

  if (!graph_data || graph_data.length === 0) return null;

  return (
    <div
      ref={wrapperRef}
      className={`dile-box barchart-container ${blur ? 'blur' : ''}`}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />
    </div>
  );
}

export default DilemaBarGraph;